import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import styled, { keyframes, css } from "styled-components";

const API_BASE_URL = "https://admin.shopaholics.ge/api";

/* ============ THEME ============ */
const COLORS = {
  card: "#ffffff",
  text: "#0f172a",
  dim: "#64748b",
  line: "rgba(0,0,0,.08)",
  brand: "#a15a5a",
  brandDark: "#8e4a4a",
  green: "#22c55e",
  red: "#ef4444",
  blue: "#3b82f6",
  shadow: "0 8px 30px rgba(2,6,23,.08)",
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ============ LAYOUT ============ */
const Wrap = styled.div`
  max-width: 1280px;
  margin: min(3vh,24px) auto;
  padding: clamp(14px, 2.8vw, 22px);
`;

const Card = styled.div`
  background: ${COLORS.card};
  border: 1px solid ${COLORS.line};
  border-radius: 16px;
  box-shadow: ${COLORS.shadow};
  padding: clamp(16px, 3vw, 22px);
  animation: ${fadeIn} .35s ease both;
`;

const Title = styled.h2`
  font-size: clamp(18px, 2.4vw, 22px);
  font-weight: 700;
  color: ${COLORS.text};
  margin: 0 0 12px;
`;

const Toolbar = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto auto;
  gap: 10px;
  align-items: center;
  margin-bottom: 12px;

  @media (max-width: 920px){
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media (max-width: 680px){
    grid-template-columns: 1fr;
  }
`;

const SearchInput = styled.input`
  border: 1px solid ${COLORS.line};
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 14px;
  outline: none;
  width: 100%;
  &:focus{ border-color: ${COLORS.brand}; box-shadow: 0 0 0 3px rgba(161,90,90,.12); }
`;

const Select = styled.select`
  border: 1px solid ${COLORS.line};
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 14px;
  background: #fff;
  outline: none;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 6px;
  font-size: 14px;

  th, td {
    border-bottom: 1px solid ${COLORS.line};
    padding: 10px 12px;
    text-align: left;
    vertical-align: middle;
  }

  th {
    font-weight: 600;
    font-size: 12.5px;
    color: ${COLORS.dim};
    text-transform: uppercase;
    letter-spacing: .02em;
    background: #fafafa;
    position: sticky; top: 0; z-index: 1;
  }

  tbody tr:hover { background: #fcfcfd; }
`;

const Badge = styled.span`
  display: inline-block;
  border-radius: 999px;
  padding: 3px 8px;
  font-size: 12px;
  color: #0f172a;
  background: #f1f5f9;
  border: 1px solid ${COLORS.line};
`;

const SaleBadge = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  color: #fff;
  background: ${COLORS.red};
`;

const ToggleBtn = styled.button`
  background: ${COLORS.blue};
  color: #fff;
  border: 0;
  padding: 8px 10px;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  &:hover{ filter: brightness(.96); }
`;

const SaleInput = styled.input`
  width: 120px;
  max-width: 46vw;
  padding: 7px 8px;
  border: 1px solid ${COLORS.line};
  border-radius: 10px;
  text-align: center;
  &:focus{ border-color: ${COLORS.brand}; box-shadow: 0 0 0 3px rgba(161,90,90,.12); outline: none; }
`;

const Btn = styled.button`
  border: 0; cursor: pointer; font-weight: 700;
  border-radius: 10px; padding: 8px 10px; color: #fff; margin-left: 6px;
  ${({ tone }) => tone === "save" && css`background:${COLORS.green};`}
  ${({ tone }) => tone === "remove" && css`background:${COLORS.red};`}
  &:hover{ filter: brightness(.96); }
`;

const Pagination = styled.div`
  display: flex; gap: 8px; align-items: center; justify-content: flex-end; margin-top: 12px;
  flex-wrap: wrap;
`;

const PageBtn = styled.button`
  min-width: 36px; height: 36px; padding: 0 10px;
  border-radius: 10px; border: 1px solid ${COLORS.line}; background: #fff; cursor: pointer;
  ${({ active }) => active && css`background:${COLORS.brand}; color:#fff; border-color:${COLORS.brandDark};`}
`;

const Hint = styled.div`
  font-size: 12.5px; color: ${COLORS.dim}; margin-top: 6px;
`;

/* ========= Helpers ========= */
const PERFUME_SET = new Set(["perfume","skin-care","hair-and-body-care","atomizers"]); // perfume-like
const PER_ML_ONLY = new Set(["skin-care","hair-and-body-care","atomizers"]);

export default function AllProductsSale() {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [saleValues, setSaleValues] = useState({}); // key by mlId | variantId | productId
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  // NEW filters
  const [saleFilter, setSaleFilter] = useState("all"); // all | on
  const [catFilter, setCatFilter]   = useState("all"); // all | perfume | perml | accessories

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  useEffect(() => { fetchData(); }, [page, pageSize, search, saleFilter, catFilter]);

  async function fetchData() {
    try {
      const params = {
        page,
        page_size: pageSize,
        search: search.trim() || undefined,
        on_sale: saleFilter === "on" ? 1 : undefined,
      };
      const { data } = await axios.get(`${API_BASE_URL}/fetch_perfumes.php`, { params, withCredentials: true });
      let list = data.items || [];

      // Client-side category filter (server already filtered sale if needed)
      if (catFilter !== "all") {
        list = list.filter(p => {
          const cat = (p.category || "").toLowerCase();
          if (catFilter === "perfume") return cat === "perfume";
          if (catFilter === "perml")   return PER_ML_ONLY.has(cat);
          if (catFilter === "accessories") return !PERFUME_SET.has(cat);
          return true;
        });
      }

      setItems(list);
      setTotal(data.total || 0);

      // შეკეთება გვერდის რიცხვზე
      const newTotalPages = Math.max(1, Math.ceil((data.total || 0) / pageSize));
      if (page > newTotalPages) setPage(newTotalPages);
    } catch (e) {
      console.error("Fetch error", e);
      setItems([]); setTotal(0);
    }
  }

  function onSaleInputChange(key, value) {
    setSaleValues(prev => ({ ...prev, [key]: value }));
  }

  async function saveSale({ product_id, ml_id, variant_id }) {
    const key = variant_id ?? ml_id ?? product_id;
    const raw = saleValues[key] ?? null;
    const sale_price = raw === null || raw === "" ? null : Number(raw);
    if (!sale_price || sale_price <= 0) { alert("Enter valid sale price"); return; }

    try {
      const body = { product_id, sale_price, ml_id: ml_id ?? null, variant_id: variant_id ?? null };
      const { data } = await axios.post(`${API_BASE_URL}/update_sale_price.php`, body, { withCredentials: true });
      if (data && data.success) {
        alert("✅ Updated");
        setItems(list => list.map(p => {
          if (p.product_id !== product_id) return p;
          if (ml_id) {
            return { ...p, milliliters: (p.milliliters||[]).map(ml => ml.id === ml_id ? { ...ml, sale_price } : ml) , has_sale: true};
          }
          if (variant_id) {
            return { ...p, accessory_variants: (p.accessory_variants||[]).map(v => v.id === variant_id ? { ...v, sale_p: sale_price } : v), has_sale: true };
          }
          return { ...p, sale_p: sale_price, has_sale: true };
        }));
      } else {
        alert(`❌ Failed: ${data?.error || "Unknown error"}`);
      }
    } catch (e) {
      console.error(e);
      alert("❌ Request failed");
    }
  }

  async function removeSale({ product_id, ml_id, variant_id }) {
    try {
      const body = { product_id, ml_id: ml_id ?? null, variant_id: variant_id ?? null };
      const { data } = await axios.post(`${API_BASE_URL}/remove_sale_price.php`, body, { withCredentials: true });
      if (data && data.success) {
        alert("Removed");
        setItems(list => list.map(p => {
          if (p.product_id !== product_id) return p;
          if (ml_id) {
            const mlNew = (p.milliliters||[]).map(ml => ml.id === ml_id ? { ...ml, sale_price: null } : ml);
            // recompute has_sale locally
            const hasMlSale = mlNew.some(m => m.sale_price != null);
            const hasVarSale = (p.accessory_variants||[]).some(v => v.sale_p != null);
            return { ...p, milliliters: mlNew, has_sale: (p.sale_p != null) || hasMlSale || hasVarSale };
          }
          if (variant_id) {
            const varNew = (p.accessory_variants||[]).map(v => v.id === variant_id ? { ...v, sale_p: null } : v);
            const hasMlSale = (p.milliliters||[]).some(m => m.sale_price != null);
            const hasVarSale = varNew.some(v => v.sale_p != null);
            return { ...p, accessory_variants: varNew, has_sale: (p.sale_p != null) || hasMlSale || hasVarSale };
          }
          const hasMlSale = (p.milliliters||[]).some(m => m.sale_price != null);
          const hasVarSale = (p.accessory_variants||[]).some(v => v.sale_p != null);
          return { ...p, sale_p: null, has_sale: hasMlSale || hasVarSale };
        }));
        const key = variant_id ?? ml_id ?? product_id;
        setSaleValues(prev => { const x = { ...prev }; delete x[key]; return x; });
      } else {
        alert(`Failed: ${data?.error || "Unknown"}`);
      }
    } catch (e) {
      console.error(e);
      alert("Request failed");
    }
  }

  function RowActions({ p }) {
    const hasML = Array.isArray(p.milliliters) && p.milliliters.length > 0;
    const hasVariants = Array.isArray(p.accessory_variants) && p.accessory_variants.length > 0;

    if (!hasML && !hasVariants) {
      const key = p.product_id;
      const val = saleValues[key] ?? (p.sale_p ?? "");
      return (
        <>
          <SaleInput
            type="number" min="0" step="0.01" placeholder="Sale"
            value={val}
            onChange={e => onSaleInputChange(key, e.target.value)}
          />
          <Btn tone="save" onClick={() => saveSale({ product_id: p.product_id })}>Save</Btn>
          {p.sale_p != null && <Btn tone="remove" onClick={() => removeSale({ product_id: p.product_id })}>Remove</Btn>}
        </>
      );
    }

    return (
      <ToggleBtn onClick={() => setExpanded(expanded === p.product_id ? null : p.product_id)}>
        {expanded === p.product_id ? "Hide" : "Show"} details
      </ToggleBtn>
    );
  }

  // pagination buttons (windowed)
  const pagerButtons = useMemo(() => {
    const pages = Math.max(1, Math.ceil(total / pageSize));
    const window = 7;
    const start = Math.max(1, Math.min(page - Math.floor(window/2), pages - (window - 1)));
    return Array.from({ length: Math.min(window, pages) }, (_, i) => start + i);
  }, [total, page, pageSize]);

  // Helper: any sale within product (for fast check in UI)
  function productHasSale(p){
    if (p.has_sale) return true; // server-computed
    if (p.sale_p != null) return true;
    if ((p.milliliters||[]).some(ml => ml.sale_price != null)) return true;
    if ((p.accessory_variants||[]).some(v => v.sale_p != null)) return true;
    return false;
  }

  return (
    <Wrap>
      <Card>
        <Title>All Products — Sale Management</Title>

        <Toolbar>
          <SearchInput
            placeholder="Search by name or product_id…"
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
          />

          {/* NEW: Sale filter */}
          <Select value={saleFilter} onChange={e => { setPage(1); setSaleFilter(e.target.value); }}>
            <option value="all">All items</option>
            <option value="on">On Sale only</option>
          </Select>

          {/* NEW: Category filter */}
          <Select value={catFilter} onChange={e => { setPage(1); setCatFilter(e.target.value); }}>
            <option value="all">All categories</option>
            <option value="perfume">Perfume</option>
            <option value="perml">Per-ML (atomizers/skin/hair)</option>
            <option value="accessories">Accessories/Other</option>
          </Select>

          <Hint>{total.toLocaleString()} items</Hint>
        </Toolbar>

        <Table>
          <thead>
            <tr>
              <th style={{width: '30%'}}>Name</th>
              <th style={{width: '16%'}}>Brand</th>
              <th style={{width: '16%'}}>Product ID</th>
              <th style={{width: '16%'}}>Category</th>
              <th style={{width: '22%'}}>Action</th>
            </tr>
          </thead>
          <tbody>
            {items.map(p => (
              <React.Fragment key={p.product_id}>
                <tr>
                  <td>
                    <div style={{display:'flex',alignItems:'center',gap:10}}>
                      {(p.cover || p.image) && (
                        <img
                          src={p.cover || p.image}
                          alt=""
                          style={{width:40,height:40,objectFit:'cover',borderRadius:8,border:'1px solid #eee'}}
                          loading="lazy"
                        />
                      )}
                      <div>
                        <div style={{fontWeight:700, display:'flex', alignItems:'center'}}>
                          {p.name}
                          {productHasSale(p) && <SaleBadge>SALE</SaleBadge>}
                        </div>
                        {!p.milliliters?.length && !p.accessory_variants?.length && p.sale_p != null && (
                          <div style={{fontSize:12,color:COLORS.dim}}>Sale (product): {p.sale_p} ₾</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>{p.brand || "-"}</td>
                  <td><Badge>{p.product_id}</Badge></td>
                  <td><Badge>{p.category}</Badge></td>
                  <td><RowActions p={p} /></td>
                </tr>

                {expanded === p.product_id && (
                  <tr>
                    <td colSpan={5}>
                      {/* Milliliters */}
                      {Array.isArray(p.milliliters) && p.milliliters.length > 0 && (
                        <div style={{border:'1px solid #eee', borderRadius:12, padding:12, marginBottom:10}}>
                          <div style={{fontWeight:700, marginBottom:6}}>Milliliters</div>
                          <Table>
                            <thead>
                              <tr>
                                <th>Size (ml)</th>
                                <th>Image</th>
                                <th>Price</th>
                                <th>Sale price</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {p.milliliters.map(ml => {
                                const key = ml.id;
                                const val = saleValues[key] ?? (ml.sale_price ?? "");
                                return (
                                  <tr key={ml.id}>
                                    <td>{ml.size}</td>
                                    <td>
                                      {ml.image && (
                                        <img
                                          src={ml.image}
                                          alt=""
                                          style={{width:38,height:38,objectFit:'cover',borderRadius:8,border:'1px solid #eee'}}
                                          loading="lazy"
                                        />
                                      )}
                                      {ml.sale_price != null && <SaleBadge style={{marginLeft:8}}>SALE</SaleBadge>}
                                    </td>
                                    <td>{ml.price} ₾</td>
                                    <td>
                                      <SaleInput
                                        type="number" min="0" step="0.01" placeholder="Sale"
                                        value={val}
                                        onChange={e => onSaleInputChange(key, e.target.value)}
                                      />
                                    </td>
                                    <td>
                                      <Btn tone="save" onClick={() => saveSale({ product_id: p.product_id, ml_id: ml.id })}>Save</Btn>
                                      {ml.sale_price != null && (
                                        <Btn tone="remove" onClick={() => removeSale({ product_id: p.product_id, ml_id: ml.id })}>Remove</Btn>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </div>
                      )}

                      {/* Accessory Variants */}
                      {Array.isArray(p.accessory_variants) && p.accessory_variants.length > 0 && (
                        <div style={{border:'1px solid #eee', borderRadius:12, padding:12}}>
                          <div style={{fontWeight:700, marginBottom:6}}>Accessory Variants</div>
                          <Table>
                            <thead>
                              <tr>
                                <th>Variant</th>
                                <th>Image</th>
                                <th>Price</th>
                                <th>Sale price</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {p.accessory_variants.map(v => {
                                const key = v.id;
                                const val = saleValues[key] ?? (v.sale_p ?? "");
                                const label = [v.color_ka || v.color || null, v.size_label || null, v.sku || null]
                                  .filter(Boolean).join(" • ");
                                return (
                                  <tr key={v.id}>
                                    <td>{label || v.id}</td>
                                    <td>
                                      {v.image && (
                                        <img
                                          src={v.image}
                                          alt=""
                                          style={{width:38,height:38,objectFit:'cover',borderRadius:8,border:'1px solid #eee'}}
                                          loading="lazy"
                                        />
                                      )}
                                      {v.sale_p != null && <SaleBadge style={{marginLeft:8}}>SALE</SaleBadge>}
                                    </td>
                                    <td>{v.price} ₾</td>
                                    <td>
                                      <SaleInput
                                        type="number" min="0" step="0.01" placeholder="Sale"
                                        value={val}
                                        onChange={e => onSaleInputChange(key, e.target.value)}
                                      />
                                    </td>
                                    <td>
                                      <Btn tone="save" onClick={() => saveSale({ product_id: p.product_id, variant_id: v.id })}>Save</Btn>
                                      {v.sale_p != null && (
                                        <Btn tone="remove" onClick={() => removeSale({ product_id: p.product_id, variant_id: v.id })}>Remove</Btn>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={5} style={{color:COLORS.dim, padding:'16px 12px'}}>Nothing found…</td></tr>
            )}
          </tbody>
        </Table>

        <Pagination>
          <PageBtn onClick={() => setPage(p => Math.max(1, p-1))} disabled={page===1}>‹</PageBtn>
          {pagerButtons.map(num => (
            <PageBtn key={num} active={num===page} onClick={()=>setPage(num)}>{num}</PageBtn>
          ))}
          <PageBtn onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page===totalPages}>›</PageBtn>
        </Pagination>
      </Card>
    </Wrap>
  );
}
